import type { Bot, DirectorNoteValue } from '@/types/domain'
import { isOpenWorldAttemptUuid } from '@/lib/openWorlds/attemptPersistence'

export interface OpenWorldConversationSettings {
  storytellerBotId?: number
  contextSize?: number
  maxTokens?: number
  temperature?: number
}

export interface OpenWorldDirectorNote extends DirectorNoteValue {
  playerMessageId: string
  updatedAt: string
}

interface ChangeBase {
  playthroughId: string
  receiptId: string
  expectedPlaythroughRevision: number
  expectedHeadId: string | null
}

export type ChangeOpenWorldConversationRequest = ChangeBase &
  (
    | { operation: 'select-response'; payload: { attemptId: string; variantId: string } }
    | { operation: 'settings'; payload: OpenWorldConversationSettings }
    | {
        operation: 'director-note'
        payload: { playerMessageId: string; note: DirectorNoteValue | null }
      }
    | {
        operation: 'post-player-with-note'
        payload: { playerMessageId: string; content: string; note: DirectorNoteValue | null }
      }
    | { operation: 'rename'; payload: { title: string } }
    | { operation: 'edit-opening'; payload: { content: string } }
  )

const record = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))
const integer = (value: unknown, min: number) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= 2_147_483_647
const boundedText = (value: unknown, max: number) =>
  typeof value === 'string' &&
  Boolean(value.trim()) &&
  new TextEncoder().encode(value).length <= max

export function validOpenWorldConversationSettings(
  value: unknown
): value is OpenWorldConversationSettings {
  return (
    record(value) &&
    Object.entries(value).every(([key, item]) => {
      if (key === 'storytellerBotId') return integer(item, 1)
      if (key === 'contextSize') return integer(item, 1024)
      if (key === 'maxTokens') return integer(item, 0)
      if (key === 'temperature')
        return typeof item === 'number' && Number.isFinite(item) && item >= 0 && item <= 2
      return false
    })
  )
}

export function validOpenWorldDirectorNote(value: unknown): value is DirectorNoteValue {
  return (
    record(value) &&
    Object.keys(value).length === 3 &&
    boundedText(value.content, 16_384) &&
    ['prepend', 'append'].includes(value.placement as string) &&
    (value.visibility_count === null || integer(value.visibility_count, 1))
  )
}

export function validOpenWorldConversationChange(
  value: unknown
): value is ChangeOpenWorldConversationRequest {
  if (
    !record(value) ||
    Object.keys(value).length !== 6 ||
    !isOpenWorldAttemptUuid(value.playthroughId) ||
    !isOpenWorldAttemptUuid(value.receiptId) ||
    !integer(value.expectedPlaythroughRevision, 1) ||
    (value.expectedHeadId !== null && !isOpenWorldAttemptUuid(value.expectedHeadId)) ||
    !record(value.payload)
  )
    return false
  const payload = value.payload
  if (value.operation === 'select-response')
    return (
      Object.keys(payload).length === 2 &&
      isOpenWorldAttemptUuid(payload.attemptId) &&
      isOpenWorldAttemptUuid(payload.variantId) &&
      payload.attemptId === value.expectedHeadId
    )
  if (value.operation === 'settings') return validOpenWorldConversationSettings(payload)
  if (value.operation === 'rename')
    return Object.keys(payload).length === 1 && boundedText(payload.title, 512)
  if (value.operation === 'edit-opening')
    return Object.keys(payload).length === 1 && boundedText(payload.content, 65_536)
  if (!['director-note', 'post-player-with-note'].includes(value.operation as string)) return false
  return (
    Object.keys(payload).length === (value.operation === 'director-note' ? 2 : 3) &&
    isOpenWorldAttemptUuid(payload.playerMessageId) &&
    (payload.note === null || validOpenWorldDirectorNote(payload.note)) &&
    (value.operation !== 'post-player-with-note' || boundedText(payload.content, 65_536))
  )
}

export function applyOpenWorldConversationSettings(
  bot: Bot,
  settings: OpenWorldConversationSettings = {}
): Bot {
  return {
    ...bot,
    context_size: settings.contextSize ?? bot.context_size,
    max_tokens: settings.maxTokens ?? bot.max_tokens,
    temperature: settings.temperature ?? bot.temperature,
  }
}
